import { Component } from '@theme/component';

/**
 * @typedef {Object} AxWplusSliderRefs
 * @property {HTMLElement[]} [carousels] - Carousel containers
 * @property {HTMLButtonElement} [previous] - Previous arrow
 * @property {HTMLButtonElement} [next] - Next arrow
 */

/**
 * Withings+ slider section — single Swiper carousel with hero content.
 * @extends {Component<AxWplusSliderRefs>}
 */
class AxWithingsPlusSliderComponent extends Component {
  /** @type {any} */
  #swiper = null;

  /** @type {HTMLElement[]} */
  #slides = [];

  async connectedCallback() {
    super.connectedCallback();

    await customElements.whenDefined('swiper-container');

    const carousel = this.refs.carousels?.[0];
    if (carousel) {
      this.#initSwiper(carousel);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    try {
      this.#swiper?.destroy(true, true);
    } catch (_) {
      // noop
    }

    this.#swiper = null;
    this.#slides = [];
  }

  previous() {
    this.#swiper?.slidePrev();
  }

  next() {
    this.#swiper?.slideNext();
  }

  /**
   * @param {HTMLElement} carousel
   */
  #initSwiper(carousel) {
    const swiperEl = /** @type {HTMLElement & { swiper: any; initialize: () => void }} */ (
      carousel.querySelector('swiper-container.ax-wplus-slider__swiper')
    );
    const fallback = carousel.querySelector('[data-ax-fallback]');
    if (!swiperEl || !fallback) return;

    const slideElements = [...fallback.children];
    if (!slideElements.length) return;

    const reservedHeight = carousel.getBoundingClientRect().height;
    if (reservedHeight > 0) {
      carousel.style.minHeight = `${reservedHeight}px`;
    }

    for (const slideEl of slideElements) {
      const swiperSlide = document.createElement('swiper-slide');
      swiperSlide.appendChild(slideEl);
      swiperEl.appendChild(swiperSlide);
    }

    this.#slides = [...swiperEl.querySelectorAll('swiper-slide')];
    fallback.remove();

    const gapDesktop = parseInt(swiperEl.dataset.gapDesktop ?? '0', 10) || 0;
    const gapMobile = parseInt(swiperEl.dataset.gapMobile ?? '0', 10) || 0;

    Object.assign(swiperEl, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      speed: 400,
      grabCursor: true,
      watchOverflow: true,
      a11y: true,
      keyboard: { enabled: true },
      spaceBetween: gapMobile,
      slidesOffsetAfter: 24,
      breakpoints: {
        750: { spaceBetween: gapDesktop, slidesOffsetAfter: 32 },
      },
    });

    swiperEl.initialize();
    swiperEl.removeAttribute('init');

    // Equal height slides — ::part(wrapper) doesn't work from scoped stylesheets
    const wrapper = swiperEl.shadowRoot?.querySelector('.swiper-wrapper');
    if (wrapper) {
      wrapper.style.alignItems = 'stretch';
    }

    requestAnimationFrame(() => {
      carousel.style.minHeight = '';
    });

    this.#swiper = swiperEl.swiper;
    if (!this.#swiper) return;

    const syncDisabled = () => {
      const { previous, next } = this.refs;
      if (previous) previous.disabled = Boolean(this.#swiper.isBeginning);
      if (next) next.disabled = Boolean(this.#swiper.isEnd);
    };

    syncDisabled();
    this.#swiper.on('slideChange', syncDisabled);
    this.#swiper.on('reachBeginning', syncDisabled);
    this.#swiper.on('reachEnd', syncDisabled);
    this.#swiper.on('fromEdge', syncDisabled);
    this.#swiper.on('resize', syncDisabled);

    this.#preloadAdjacent(0, { currentOnly: true });

    const preloadNeighbors = () => {
      this.#preloadAdjacent(this.#swiper.realIndex ?? 0);
    };

    this.#swiper.on('touchStart', preloadNeighbors);
    this.#swiper.on('slideChangeTransitionStart', preloadNeighbors);

    this.#swiper.update();
  }

  /**
   * @param {number} index
   * @param {{ currentOnly?: boolean }} [options]
   */
  #preloadAdjacent(index, { currentOnly = false } = {}) {
    if (!this.#slides.length) return;

    const offsets = currentOnly ? [0] : [0, 1, -1];

    for (const offset of offsets) {
      const target = this.#slides[index + offset];
      if (target?.querySelector('[data-lazy-keep]')) {
        document.dispatchEvent(
          new CustomEvent('removeLazyKeep', {
            detail: { lazyParentElement: target },
          })
        );
      }
    }
  }
}

customElements.define('ax-withings-plus-slider-component', AxWithingsPlusSliderComponent);
